import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import type {
  BenchHardwareFound,
  BenchPlanPreview,
  BenchPlanSearchResult,
  BenchPlanSelectionRequest,
  BenchPlanSelectionState,
  BenchStageDeselection,
} from '@siliconx/shared';
import { qk } from '../../lib/queryKeys';
import {
  getBenchMandatorySet,
  isPlansDisabled,
  planErrorCode,
  planErrorStatus,
  previewBenchPlan,
  searchBenchPlans,
} from '../../lib/benchPlans';
import { AgentText } from './AgentText';
import {
  areaLabel,
  summarizeAssetImpact,
  fitBadges,
  formatDuration,
  gapCopy,
  groupPreview,
  isNotRetryable,
  kindLabel,
  orderAreas,
  ownerLabel,
  planErrorMessage,
  previewChanges,
  serviceTagConfirmed,
  serviceTagTail,
} from './planModel';

/** What the approval page needs from the plan section: may it approve, with which plan, and the confirmation line. */
export interface PlanApprovalState {
  /** True once a preview of the chosen plan is on screen and every confirmation it needs is given. */
  ready: boolean;
  /** The `plan` of the approve body; null until a preview exists. */
  plan: BenchPlanSelectionRequest | null;
  /** "You selected: <name> v<N> (<owner>)" parts; null until a plan is chosen. */
  chosen: { name: string; version: number; owner: 'siliconx' | 'org' } | null;
  /** Why approval is blocked when it is (shown by the section itself). */
  blocked: boolean;
  /** SIL-2458: the approver ticked "Record inventory only, test nothing"; sent as `acknowledgeNoTests` (only ever true when the preview runs nothing). */
  acknowledgeNoTests: boolean;
}

export const NO_PLAN_STATE: PlanApprovalState = { ready: false, plan: null, chosen: null, blocked: false, acknowledgeNoTests: false };

const SEARCH_DEBOUNCE_MS = 400;
const PREVIEW_DEBOUNCE_MS = 300;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function OwnerPill({ origin }: { origin: 'siliconx' | 'org' }) {
  return <StatusPill tone={origin === 'siliconx' ? 'neutral' : 'green'}>{ownerLabel(origin)}</StatusPill>;
}

function HardwareFound({ hardware }: { hardware: BenchHardwareFound }) {
  const areas = orderAreas(hardware.parts.map((p) => p.area));
  return (
    <section aria-label="Hardware found" className="space-y-2">
      <PanelTitle title="Hardware found" description="Reported by the bench. Serial numbers are masked." />
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Host</dt>
        <dd>
          <AgentText value={[hardware.host.vendor, hardware.host.model].filter(Boolean).join(' ') || null} />
        </dd>
        <dt className="text-muted-foreground">Service tag</dt>
        <dd><AgentText value={hardware.host.serviceTag} /></dd>
      </dl>
      {hardware.parts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No parts were reported.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {areas.map((area) => {
            const parts = hardware.parts.filter((p) => p.area === area);
            if (parts.length === 0) return null;
            return (
              <li key={area}>
                <span className="font-medium">{areaLabel(area)}</span>{' '}
                <span className="text-muted-foreground">({parts.length})</span>
                <ul className="ml-4 list-disc text-xs">
                  {parts.map((p) => (
                    <li key={p.key}>
                      <AgentText value={[p.vendor, p.model].filter(Boolean).join(' ') || null} />{' '}
                      <span className="text-muted-foreground">serial</span> <AgentText value={p.serialMasked} />
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function PreviewView({ preview, hardware, deselectedNote }: { preview: BenchPlanPreview; hardware: BenchHardwareFound; deselectedNote: boolean }) {
  const groups = groupPreview(preview, hardware.parts);
  const impact = summarizeAssetImpact(preview);
  return (
    <div className="space-y-3" data-testid="plan-preview">
      {impact && (
        <div className="rounded-md border border-border bg-muted/40 p-4" data-testid="asset-impact">
          <p className="text-base font-semibold">
            {impact.newCount === 0 ? 'No new assets will be created.' : `${impact.newCount} new ${impact.newCount === 1 ? 'asset' : 'assets'} will be created.`}
          </p>
          {impact.newCategories && <p className="text-sm">{impact.newCategories}</p>}
          {impact.existingCount > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              {impact.existingCount} already in your inventory (will be updated).
            </p>
          )}
        </div>
      )}
      {groups.length === 0 && <p className="text-sm text-muted-foreground">No tests apply to this machine under this plan.</p>}
      {groups.map((g) => (
        <div key={g.deviceKey} className="rounded-md border border-border p-3">
          <p className="text-sm font-medium">
            {g.part ? areaLabel(g.part.area) : 'Device'}{' '}
            <AgentText className="text-xs font-normal" value={g.part ? [g.part.vendor, g.part.model].filter(Boolean).join(' ') || null : g.deviceKey} />
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {g.steps.map((s) => (
              <li key={`${s.deviceKey}:${s.stepKind}:${s.origin}`} className="flex flex-wrap items-center gap-2">
                {s.origin === 'mandatory' ? (
                  <span className="inline-flex items-center gap-1" title="Required by SiliconX. Cannot be removed.">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span className="sr-only">Required by SiliconX</span>
                  </span>
                ) : (
                  <span className="inline-block h-3.5 w-3.5" aria-hidden />
                )}
                <span className={s.deselected ? 'text-muted-foreground line-through' : ''}>{kindLabel(s.stepKind)}</span>
                {s.origin === 'mandatory' && <StatusPill tone="neutral">SiliconX</StatusPill>}
                {s.origin === 'org' && <StatusPill tone="green">Org</StatusPill>}
                {s.deselected && <span className="text-xs text-muted-foreground">removed, will not run</span>}
                {s.status === 'gap' && !s.deselected && <StatusPill tone="amber">{gapCopy()}</StatusPill>}
                {s.destructive && !s.deselected && <StatusPill tone="red">Wipes data</StatusPill>}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <ul className="space-y-0.5 text-sm text-muted-foreground">
        <li>
          {preview.outOfScope === 0
            ? 'No parts are left out of scope.'
            : `${preview.outOfScope} ${preview.outOfScope === 1 ? 'part' : 'parts'} recorded, not tested (out of scope).`}
        </li>
        {preview.gaps > 0 && (
          <li>{preview.gaps === 1 ? '1 gap: no SiliconX test for this part yet.' : `${preview.gaps} gaps: no SiliconX test for these parts yet.`}</li>
        )}
        <li>Estimated duration: {formatDuration(preview.estimatedSeconds)}.</li>
        {deselectedNote && <li>Removed tests cap the grade at Normal wear.</li>}
      </ul>
    </div>
  );
}

/**
 * SIL-2246 U1. The plan half of the one-page approval: hardware found, plan search with fit badges, the live "what will run"
 * preview (recomputed on plan or tick change, debounced), the WILL WIPE block with the host service tag confirmation (O7) and
 * the SiliconX mandatory-rules line. Controlled by the pair page through `onState`. The server owns every decision: this
 * panel only shows what the preview said and echoes its digest back through `plan`.
 *
 * Degrades: a 409 `plans_disabled` from search or preview calls `onPlansDisabled` and the page falls back to the legacy
 * approval. An agent string is only ever rendered through AgentText.
 */
export function PlanApprovalSection({
  code,
  hardware,
  selection,
  deselections,
  disabled,
  reviewNonce,
  onState,
  onPlansDisabled,
}: {
  code: string;
  hardware: BenchHardwareFound;
  selection: BenchPlanSelectionState;
  /** The admin's unticked org tests (existing test selection panel). Empty for a member. */
  deselections: BenchStageDeselection[];
  disabled: boolean;
  /** Bumped by the page when approve answered preview_stale / plan_stale: refetch the preview and show what changed. */
  reviewNonce: number;
  onState: (s: PlanApprovalState) => void;
  onPlansDisabled: () => void;
}) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), SEARCH_DEBOUNCE_MS);
  const [picked, setPicked] = useState<{ id: string; version: number } | null>(null);
  const [typedTag, setTypedTag] = useState('');
  const [changes, setChanges] = useState<string[] | null>(null);
  const [ackNoTests, setAckNoTests] = useState(false);
  const shown = useRef<BenchPlanPreview | null>(null);
  const preselected = useRef(false);

  const search = useQuery({
    queryKey: qk.bench.planSearch(code, dq),
    queryFn: () => searchBenchPlans(dq, code),
    retry: false,
    staleTime: 60_000,
  });
  const mandatory = useQuery({
    queryKey: qk.bench.mandatorySet(),
    queryFn: getBenchMandatorySet,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const results = search.data ?? [];
  // Resolve the chosen plan from the latest results; keep the last known row when a new search no longer lists it.
  const lastChosen = useRef<BenchPlanSearchResult | null>(null);
  const found = picked ? results.find((r) => r.id === picked.id && r.version === picked.version) : undefined;
  if (found) lastChosen.current = found;
  const chosenRow = picked && lastChosen.current && lastChosen.current.id === picked.id ? lastChosen.current : null;

  // Preselect the default plan once, never one that wipes (a destructive plan is a deliberate choice).
  useEffect(() => {
    if (preselected.current || picked || !search.data) return;
    const def = selection.defaultPlan;
    const row = def ? search.data.find((r) => r.id === def.id && r.version === def.version) : undefined;
    preselected.current = true;
    if (row && !row.includesWipe) setPicked({ id: row.id, version: row.version });
  }, [search.data, selection.defaultPlan, picked]);

  const deselKey = useMemo(() => JSON.stringify(deselections), [deselections]);
  const debouncedDesel = useDebounced(deselKey, PREVIEW_DEBOUNCE_MS);
  const settled = debouncedDesel === deselKey;
  const previewDeselections = useMemo(() => JSON.parse(debouncedDesel) as BenchStageDeselection[], [debouncedDesel]);

  const preview = useQuery({
    queryKey: qk.bench.planPreview(code, picked?.id ?? '', picked?.version ?? 0, debouncedDesel),
    queryFn: () =>
      previewBenchPlan(code, {
        planId: picked!.id,
        version: picked!.version,
        ...(previewDeselections.length > 0 ? { deselections: previewDeselections } : {}),
      }),
    enabled: picked !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const searchDisabled = search.isError && isPlansDisabled(search.error);
  const previewDisabled = preview.isError && isPlansDisabled(preview.error);
  useEffect(() => {
    if (searchDisabled || previewDisabled) onPlansDisabled();
  }, [searchDisabled, previewDisabled, onPlansDisabled]);

  // Approve refused the digest: show what the fresh preview says changed.
  useEffect(() => {
    if (reviewNonce === 0 || !picked) return;
    const before = shown.current;
    void preview.refetch().then((r) => {
      if (r.data && before) setChanges(previewChanges(before, r.data));
      else if (r.data) setChanges(['What will run was updated.']);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewNonce]);

  useEffect(() => {
    if (preview.data) shown.current = preview.data;
  }, [preview.data]);

  // A different plan or a different wipe set voids the typed service tag.
  const wipeKey = preview.data ? preview.data.wipeDevices.join(',') : '';
  useEffect(() => setTypedTag(''), [picked?.id, picked?.version, wipeKey]);

  // SIL-2458: a preview that runs nothing blocks approval behind an explicit acknowledgment. The server enforces it too (409
  // `no_runnable_test`); a different plan or a changed preview voids the tick, like the wipe confirmation.
  const noRunnable = preview.data && settled ? preview.data.runnableSteps === 0 : false;
  useEffect(() => setAckNoTests(false), [picked?.id, picked?.version, debouncedDesel, preview.data?.previewDigest]);

  const data = preview.data && settled ? preview.data : null;
  const wipeCount = data?.wipeDevices.length ?? 0;
  const tail = serviceTagTail(hardware.host.serviceTag);
  const tagOk = wipeCount === 0 || serviceTagConfirmed(hardware.host.serviceTag, typedTag);
  const noMandatory = isNotRetryable(planErrorCode(preview.error)) || isNotRetryable(planErrorCode(mandatory.error)) ||
    (mandatory.isError && planErrorStatus(mandatory.error) === 404 && planErrorCode(mandatory.error) === 'no_mandatory_set');
  const unsupported = !selection.supported;

  const state = useMemo<PlanApprovalState>(() => {
    const row = chosenRow;
    const blocked = noMandatory || unsupported;
    if (!picked || !row || !data || blocked) {
      return { ready: false, plan: null, chosen: row ? { name: row.name, version: row.version, owner: row.origin } : null, blocked, acknowledgeNoTests: false };
    }
    return {
      ready: tagOk && !disabled && (!noRunnable || ackNoTests),
      plan: { planId: data.planId, version: data.version, mandatorySetVersion: data.mandatorySetVersion, previewDigest: data.previewDigest },
      chosen: { name: row.name, version: row.version, owner: row.origin },
      blocked: false,
      acknowledgeNoTests: noRunnable && ackNoTests,
    };
  }, [picked, chosenRow, data, noMandatory, unsupported, tagOk, disabled, noRunnable, ackNoTests]);
  useEffect(() => onState(state), [state, onState]);

  if (searchDisabled || previewDisabled) return null;

  const searchErr = search.isError ? (planErrorMessage(planErrorCode(search.error), planErrorStatus(search.error)) ?? 'Could not search plans. Try again.') : null;
  const previewErr = preview.isError && !noMandatory
    ? (planErrorMessage(planErrorCode(preview.error), planErrorStatus(preview.error)) ?? 'Could not load what will run. Try again.')
    : null;

  return (
    <div className="space-y-5" data-testid="plan-approval">
      <HardwareFound hardware={hardware} />

      {unsupported && (
        <InsightBanner tone="amber" lead="This bench image does not support plans.">
          Update the bench image, then pair again.{selection.required ? ' A plan is required, so this machine cannot be approved yet.' : ' You can still approve without a plan.'}
        </InsightBanner>
      )}
      {noMandatory && (
        <InsightBanner tone="amber" lead="SiliconX has not published its mandatory tests yet.">
          A plan cannot be approved until it does. This will not fix itself on retry. Contact SiliconX support.
        </InsightBanner>
      )}

      {!unsupported && (
        <section aria-label="Plan" className="space-y-2">
          <PanelTitle title="Plan" description="Choose what runs on this machine. SiliconX mandatory tests are always included." />
          <label htmlFor="bench-plan-search" className="sr-only">Search plans</label>
          <input
            id="bench-plan-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            maxLength={64}
            autoComplete="off"
            placeholder="Search plans by name"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          {search.isPending && <p className="text-sm text-muted-foreground">Loading plans...</p>}
          {searchErr && <p role="alert" className="text-sm text-status-red">{searchErr}</p>}
          {search.data && results.length === 0 && (
            <p className="text-sm text-muted-foreground">{dq ? 'No plan matches that name.' : 'No plans are available yet.'}</p>
          )}
          {results.length > 0 && (
            <ul role="radiogroup" aria-label="Plans" className="max-h-72 space-y-1 overflow-y-auto">
              {results.map((r) => {
                const on = picked?.id === r.id && picked.version === r.version;
                return (
                  <li key={`${r.id}:${r.version}`}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={disabled}
                      onClick={() => {
                        setPicked({ id: r.id, version: r.version });
                        setChanges(null);
                      }}
                      className={`w-full rounded-md border px-3 py-2 text-left text-sm ${on ? 'border-brand-600 bg-muted' : 'border-border hover:bg-muted'}`}
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{r.name}</span>
                        <OwnerPill origin={r.origin} />
                        <span className="text-xs text-muted-foreground">v{r.version}</span>
                        {fitBadges(r.fit).map((b) => <StatusPill key={b.text} tone={b.tone}>{b.text}</StatusPill>)}
                        {r.includesWipe && <StatusPill tone="red">includes wipe</StatusPill>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {selection.required && !picked && !noMandatory && (
            <p className="text-xs text-muted-foreground">Your organization requires a plan for every approval.</p>
          )}
        </section>
      )}

      {picked && !unsupported && (
        <section aria-label="What will run" className="space-y-3">
          <PanelTitle title="What will run" description="Recomputed when you change the plan or untick a test." />
          {changes && (
            <InsightBanner tone="amber" lead="What will run changed. Review it again.">
              <ul className="list-disc pl-4">{changes.map((c) => <li key={c}>{c}</li>)}</ul>
            </InsightBanner>
          )}
          {(preview.isPending || preview.isFetching || !settled) && <p className="text-sm text-muted-foreground">Working out what will run...</p>}
          {previewErr && <p role="alert" className="text-sm text-status-red">{previewErr}</p>}
          {data && <PreviewView preview={data} hardware={hardware} deselectedNote={data.steps.some((s) => s.deselected)} />}

          {data && noRunnable && (
            <div role="group" aria-label="Nothing will be tested" className="space-y-2" data-testid="no-runnable-test">
              <InsightBanner tone="amber" lead="Nothing will be tested on this machine with this plan.">
                The run would record the hardware inventory and run no test, and the result would carry no test evidence. Choose another
                plan, or acknowledge below to record inventory only.
              </InsightBanner>
              <label htmlFor="bench-no-tests-ack" className="flex items-center gap-2 text-sm font-medium">
                <input
                  id="bench-no-tests-ack"
                  type="checkbox"
                  checked={ackNoTests}
                  disabled={disabled}
                  onChange={(e) => setAckNoTests(e.target.checked)}
                />
                Record inventory only, test nothing
              </label>
            </div>
          )}

          {data && wipeCount > 0 && (
            <div role="group" aria-label="Will wipe" className="space-y-2 rounded-md border border-status-red/40 bg-status-red/10 p-3">
              <p className="text-sm font-semibold text-status-red">
                WILL WIPE {wipeCount} {wipeCount === 1 ? 'device' : 'devices'}
              </p>
              <ul className="space-y-0.5 text-sm">
                {data.wipeDevices.map((k) => {
                  const part = hardware.parts.find((p) => p.key === k);
                  return (
                    <li key={k}>
                      <AgentText value={part ? [part.vendor, part.model].filter(Boolean).join(' ') || null : k} />{' '}
                      <span className="text-muted-foreground">serial</span> <AgentText value={part?.serialMasked ?? null} />
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs">
                The wipe code is still entered later, per device, during the run. To confirm this is the machine you mean, type the
                last 4 characters of the host service tag.
              </p>
              {tail === null ? (
                <p role="alert" className="text-sm text-status-red">
                  The bench reported no service tag for this host, so a plan that wipes cannot be approved here. Choose a plan without a wipe.
                </p>
              ) : (
                <>
                  <label htmlFor="bench-wipe-confirm" className="block text-sm font-medium">
                    Host service tag, last 4 characters
                  </label>
                  <input
                    id="bench-wipe-confirm"
                    value={typedTag}
                    onChange={(e) => setTypedTag(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={8}
                    className="w-40 rounded-md border border-border bg-background px-3 py-2 font-mono text-sm uppercase"
                  />
                </>
              )}
            </div>
          )}

          {data && (
            <p className="text-xs text-muted-foreground">
              {mandatory.data
                ? `SiliconX mandatory rules version ${mandatory.data.version}, effective ${new Date(mandatory.data.effectiveAt).toISOString().slice(0, 10)}.`
                : `SiliconX mandatory rules version ${data.mandatorySetVersion}.`}
            </p>
          )}
        </section>
      )}

    </div>
  );
}
